import { useMemo } from 'react';
import type { RenderedViewLine } from '../../types/financialStatement';
import { ViewLineRow } from './ViewLineRow';
import { AlertTriangle, CheckCircle } from 'lucide-react';

interface Props {
    lines: RenderedViewLine[];
    year: number;
    comparisonYear: number;
    onLineClick: (lineNr: number, displayName: string) => void;
    onCommentClick: (lineNr: number, displayName: string) => void;
    loading: boolean;
    hideZeros: boolean;
}

function formatAmount(amount: number): string {
    return new Intl.NumberFormat('de-CH', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
}

export function BalanceSheetTab({
    lines, year, comparisonYear,
    onLineClick, onCommentClick, loading, hideZeros,
}: Props) {
    const filteredLines = useMemo(() => {
        if (!hideZeros) return lines;
        return lines.filter(l =>
            l.RowType === 'Header' || l.RowType === 'Spacer' || l.RowType === 'Subtotal' || l.RowType === 'Difference'
            || l.CurrentYearAmount !== 0 || l.ComparisonYearAmount !== 0
        );
    }, [lines, hideZeros]);

    // Find the Difference row for balance check
    const diffRow = lines.find(l => l.RowType === 'Difference');
    const balanceDifference = diffRow?.CurrentYearAmount ?? 0;
    const isBalanced = Math.abs(balanceDifference) < 1;

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Balance check */}
            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm ${
                isBalanced
                    ? 'bg-green-50 text-green-700'
                    : 'bg-red-50 text-red-700'
            }`}>
                {isBalanced
                    ? <><CheckCircle className="h-4 w-4" /> Balance sheet is balanced</>
                    : <><AlertTriangle className="h-4 w-4" /> Difference: {formatAmount(balanceDifference)} CHF</>
                }
            </div>

            <div className="overflow-x-auto">
                <table className="w-auto">
                    <thead>
                        <tr className="border-b-2 border-gray-200">
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-16">Nr</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-[500px]">Description</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-32">{year}</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-32">{comparisonYear}</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-28">Variance</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-20">%</th>
                            <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase w-12"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {filteredLines.map(line => (
                            <ViewLineRow
                                key={line.SortOrder}
                                line={line}
                                onLineClick={onLineClick}
                                onCommentClick={onCommentClick}
                            />
                        ))}
                    </tbody>
                </table>
            </div>

            {lines.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                    No balance sheet data available for the selected period.
                </div>
            )}
        </div>
    );
}
